import React, { useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import "../../styles/BaseModal.css";

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  showCloseButton?: boolean;
  maxHeight?: string;
}

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = "490px",
  showCloseButton = false,
  maxHeight = "85vh",
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="bm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bm-modal"
        style={{ width: `min(${width}, 96%)`, maxHeight }}
      >
        <div className="bm-gradient-bar" />

        {title && (
          <div className="bm-header">
            <h3 className="bm-title">{title}</h3>
            {showCloseButton && (
              <button className="bm-close-btn" onClick={onClose}>
                ×
              </button>
            )}
          </div>
        )}

        <div className="bm-content">{children}</div>

        {footer && <div className="bm-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default BaseModal;
